import React from 'react';
import { Box, type BoxProps } from '@mui/material';
import type { ResponsiveStyleValue } from '@mui/system';
import { BRAND_DASHBOARD_MARK_SRC, BRAND_LOGO_SRC } from '../constants/brand';
import BrandProductName from './BrandProductName';

export type BrandLockupVariant = 'full' | 'compact';

export interface BrandLockupProps extends BoxProps {
  /** `full` matches the launch screen logo; `compact` is symbol + two-line name for headers. */
  variant?: BrandLockupVariant;
  /** Pixel width for the logo image (supports breakpoint objects in compact variant). */
  logoWidth?: ResponsiveStyleValue<number | string>;
  /** Shows Measure · Report · Reduce under the product name (compact header lockup). */
  showTagline?: boolean;
  /** Shows baseline software version below the tagline (compact header lockup). */
  showVersion?: boolean;
  /** Override image source for the full lockup (defaults to {@link BRAND_LOGO_SRC}). */
  logoSrc?: string;
}

/**
 * Logo and product name lockup aligned with the application launch screen.
 */
const BrandLockup: React.FC<BrandLockupProps> = ({
  variant = 'full',
  logoWidth,
  showTagline = false,
  showVersion = false,
  logoSrc,
  sx,
  ...rest
}) => {
  if (variant === 'full') {
    const width = logoWidth ?? 'min(520px, 92vw)';
    const imageSrc = logoSrc ?? BRAND_LOGO_SRC;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mx: 'auto',
          ...sx,
        }}
        {...rest}
      >
        <Box
          component="img"
          src={imageSrc}
          alt="sustainow Carbon Intelligence"
          sx={{
            width,
            height: 'auto',
            maxWidth: '100%',
            display: 'block',
          }}
        />
      </Box>
    );
  }

  const markWidth = logoWidth ?? 96;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        ...sx,
      }}
      {...rest}
    >
      <Box
        component="img"
        src={BRAND_DASHBOARD_MARK_SRC}
        alt="sustainow Carbon Intelligence"
        sx={{
          width: markWidth,
          height: 'auto',
          flexShrink: 0,
          objectFit: 'contain',
        }}
      />
      <BrandProductName size="compact" showTagline={showTagline} showVersion={showVersion} />
    </Box>
  );
};

export default BrandLockup;
